/**
 * Chat Message Component
 * Individual message bubble for the cat chat
 */

import { cn } from '@vsat/shared/lib/utils';

export interface Message {
  id: string;
  text: string;
  sender: 'cat' | 'user';
  timestamp: Date;
}

interface ChatMessageProps {
  message: Message;
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isCat = message.sender === 'cat';

  return (
    <div className={cn('flex gap-2 animate-fade-in', isCat ? 'justify-start' : 'justify-end')}>
      {/* Cat avatar */}
      {isCat && (
        <div className='w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-amber-100 dark:bg-amber-900/30'>
          <img src='/mascot/cat-wave.png' alt='Mèo Học Bá' className='w-full h-full object-cover' />
        </div>
      )}

      {/* Message bubble */}
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-4 py-2 shadow-sm',
          isCat
            ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-tl-sm'
            : 'bg-blue-500 text-white rounded-tr-sm'
        )}
      >
        <p className='text-sm whitespace-pre-wrap leading-relaxed'>{message.text}</p>
        <span
          className={cn(
            'text-[10px] mt-1 block',
            isCat ? 'text-gray-500 dark:text-gray-400' : 'text-blue-100'
          )}
        >
          {message.timestamp.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}

/**
 * Typing indicator component
 */
export function TypingIndicator() {
  return (
    <div className='flex gap-2 justify-start animate-fade-in'>
      <div className='w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-amber-100 dark:bg-amber-900/30'>
        <img src='/mascot/cat-wave.png' alt='Mèo Học Bá' className='w-full h-full object-cover' />
      </div>
      <div className='bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-tl-sm px-4 py-3'>
        <div className='flex gap-1'>
          <span className='w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:0ms]' />
          <span className='w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:150ms]' />
          <span className='w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:300ms]' />
        </div>
      </div>
    </div>
  );
}

export default ChatMessage;
